import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import comprobanteService from '../../services/comprobanteService';
import categoriaService from '../../services/categoriaService';
import proveedorService from '../../services/proveedorService';
import { useToast } from '../../context/ToastContext';
import Paginacion from '../../components/common/Paginacion';
import usePaginacion from '../../hooks/usePaginacion';
import { formatearMoneda, formatearFecha } from '../../utils/helpers';
import { FaPlus, FaEdit, FaTrash, FaFileInvoice, FaSearch, FaEye, FaTimes } from 'react-icons/fa';

const hoy = (() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
})();

const FORM_INICIAL = {
  tipo: '', monto_total: '', igv: '0',
  fecha_emision: hoy, moneda: 'PEN',
  numero_comprobante: '', serie: '',
  categoria_id: '', proveedor_id: '',
  descripcion: '',
};

export default function ComprobantesPage() {
  const { mostrar }                             = useToast();
  const [comprobantes, setComprobantes]         = useState([]);
  const [categorias,   setCategorias]           = useState([]);
  const [proveedores,  setProveedores]          = useState([]);
  const [cargando,     setCargando]             = useState(true);
  const [guardando,    setGuardando]            = useState(false);
  const [mostrarModal, setMostrarModal]         = useState(false);
  const [editando,     setEditando]             = useState(null);
  const [busqueda,     setBusqueda]             = useState('');
  const [filtroTipo,   setFiltroTipo]           = useState('');
  const [filtroEstado, setFiltroEstado]         = useState('');
  const [form,         setForm]                 = useState(FORM_INICIAL);
  const [errores,      setErrores]              = useState({});
  const [vistaPrevia,  setVistaPrevia]          = useState(null);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      const [c, cat, prov] = await Promise.all([
        comprobanteService.listar(),
        categoriaService.listar(),
        proveedorService.listar(),
      ]);
      setComprobantes(c.data || []);
      setCategorias(cat);
      setProveedores(prov);
    } finally {
      setCargando(false);
    }
  };

  // URL con token para archivos protegidos
    const urlArchivo = (archivo_url) => {
        const token = localStorage.getItem('token');
        return `https://sistemafinanciero.digitaleseduproperu.com/api/archivos/${archivo_url.split('/').pop()}?token=${token}`;
    };

  const comprobantesFiltrados = comprobantes.filter((c) => {
    const coincideBusqueda = !busqueda ||
      c.numero_comprobante?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.descripcion?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.serie?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.proveedor?.razon_social?.toLowerCase().includes(busqueda.toLowerCase());
    const coincideTipo   = !filtroTipo   || c.tipo   === filtroTipo;
    const coincideEstado = !filtroEstado || c.estado === filtroEstado;
    return coincideBusqueda && coincideTipo && coincideEstado;
  });

  const { datosPaginados, pagina, totalPaginas, cambiarPagina, setPagina } =
    usePaginacion(comprobantesFiltrados);

  const abrirModal = (comprobante = null) => {
    setEditando(comprobante);
    setErrores({});
    setArchivoSeleccionado(null);
    setForm(comprobante ? {
      tipo:               comprobante.tipo,
      monto_total:        comprobante.monto_total,
      igv:                comprobante.igv,
      fecha_emision:      comprobante.fecha_emision?.split('T')[0] || hoy,
      moneda:             comprobante.moneda,
      numero_comprobante: comprobante.numero_comprobante || '',
      serie:              comprobante.serie || '',
      categoria_id:       comprobante.categoria_id || '',
      proveedor_id:       comprobante.proveedor_id || '',
      descripcion:        comprobante.descripcion || '',
    } : FORM_INICIAL);
    setMostrarModal(true);
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setEditando(null);
    setForm(FORM_INICIAL);
    setErrores({});
    setArchivoSeleccionado(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errores[name]) setErrores((prev) => ({ ...prev, [name]: null }));
  };

  const validar = () => {
    const e = {};
    if (!form.tipo) e.tipo = 'Selecciona un tipo.';
    if (!form.monto_total || parseFloat(form.monto_total) <= 0)
      e.monto_total = 'El monto debe ser mayor a cero.';
    if (!form.fecha_emision) e.fecha_emision = 'La fecha es obligatoria.';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const erroresForm = validar();
    if (Object.keys(erroresForm).length > 0) {
      setErrores(erroresForm);
      return;
    }
    setGuardando(true);
    try {
      if (archivoSeleccionado instanceof File) {
        const formData = new FormData();
        formData.append('tipo',          form.tipo);
        formData.append('monto_total',   form.monto_total);
        formData.append('igv',           form.igv || '0');
        formData.append('fecha_emision', form.fecha_emision);
        formData.append('moneda',        form.moneda || 'PEN');
        if (form.numero_comprobante) formData.append('numero_comprobante', form.numero_comprobante);
        if (form.serie)              formData.append('serie',              form.serie);
        if (form.categoria_id)       formData.append('categoria_id',       form.categoria_id);
        if (form.proveedor_id)       formData.append('proveedor_id',       form.proveedor_id);
        if (form.descripcion)        formData.append('descripcion',        form.descripcion);
        formData.append('archivo', archivoSeleccionado, archivoSeleccionado.name);
        if (editando) {
          await comprobanteService.actualizar(editando.id, formData);
        } else {
          await comprobanteService.crear(formData);
        }
      } else {
        const datos = {
          tipo:               form.tipo,
          monto_total:        form.monto_total,
          igv:                form.igv || '0',
          fecha_emision:      form.fecha_emision,
          moneda:             form.moneda || 'PEN',
          numero_comprobante: form.numero_comprobante || undefined,
          serie:              form.serie || undefined,
          categoria_id:       form.categoria_id || undefined,
          proveedor_id:       form.proveedor_id || undefined,
          descripcion:        form.descripcion || undefined,
        };
        if (editando) {
          await comprobanteService.actualizarJson(editando.id, datos);
        } else {
          await comprobanteService.crearJson(datos);
        }
      }
      mostrar(editando ? 'Comprobante actualizado correctamente.' : 'Comprobante registrado correctamente.');
      cerrarModal();
      cargarDatos();
    } catch (err) {
      mostrar(err.response?.data?.message || 'Error al guardar.', 'danger');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar este comprobante?')) return;
    try {
      await comprobanteService.eliminar(id);
      mostrar('Comprobante eliminado correctamente.');
      cargarDatos();
    } catch {
      mostrar('Error al eliminar.', 'danger');
    }
  };

  return (
    <Layout>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <h4>Comprobantes</h4>
          <p>Registro y gestión de comprobantes digitales</p>
        </div>
        <button className="btn btn-danger" onClick={() => abrirModal()}>
          <FaPlus className="me-2" /> Nuevo Comprobante
        </button>
      </div>

      <div className="card card-custom mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-center">
            <div className="col-md-4">
              <div className="input-group input-group-sm">
                <span className="input-group-text"><FaSearch /></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por serie, número, proveedor..."
                  value={busqueda}
                  onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                />
              </div>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filtroTipo}
                onChange={(e) => { setFiltroTipo(e.target.value); setPagina(1); }}
              >
                <option value="">Todos los tipos</option>
                <option value="factura">Factura</option>
                <option value="boleta">Boleta</option>
                <option value="recibo">Recibo</option>
                <option value="nota_credito">Nota de Crédito</option>
              </select>
            </div>
            <div className="col-md-2">
              <select
                className="form-select form-select-sm"
                value={filtroEstado}
                onChange={(e) => { setFiltroEstado(e.target.value); setPagina(1); }}
              >
                <option value="">Todos los estados</option>
                <option value="activo">Activo</option>
                <option value="pendiente">Pendiente</option>
                <option value="anulado">Anulado</option>
              </select>
            </div>
            <div className="col-md-2">
              <span className="badge bg-secondary">
                {comprobantesFiltrados.length} registros
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-custom">
        <div className="card-body">
          {cargando ? (
            <div className="text-center py-4">
              <div className="spinner-custom mx-auto" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-custom">
                  <thead>
                    <tr>
                      <th>Serie/N°</th>
                      <th>Tipo</th>
                      <th>Categoría</th>
                      <th>Monto Total</th>
                      <th>IGV</th>
                      <th>Monto Neto</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosPaginados.length > 0 ? (
                      datosPaginados.map((c) => (
                        <tr key={c.id}>
                          <td className="fw-bold">
                            {c.serie}-{c.numero_comprobante || '—'}
                          </td>
                          <td>
                            <span className="badge bg-secondary">{c.tipo}</span>
                          </td>
                          <td>{c.categoria?.nombre || '—'}</td>
                          <td className="fw-bold">{formatearMoneda(c.monto_total)}</td>
                          <td>{formatearMoneda(c.igv)}</td>
                          <td className="text-primary fw-bold">
                            {formatearMoneda(c.monto_neto)}
                          </td>
                          <td>{formatearFecha(c.fecha_emision)}</td>
                          <td>
                            <span className={`badge ${
                              c.estado === 'activo'    ? 'badge-activo'    :
                              c.estado === 'pendiente' ? 'badge-pendiente' :
                              'badge-anulado'
                            }`}>
                              {c.estado}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <button
                                className="btn btn-sm btn-outline-info"
                                onClick={() => setVistaPrevia(c)}
                                title="Ver archivo"
                                disabled={!c.archivo_url || c.archivo_url.trim() === ''}
                              >
                                <FaEye />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => abrirModal(c)}
                                title="Editar"
                              >
                                <FaEdit />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleEliminar(c.id)}
                                title="Eliminar"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center text-muted py-4">
                          <FaFileInvoice size={32} className="mb-2 d-block mx-auto" />
                          Sin comprobantes registrados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Paginacion
                paginaActual={pagina}
                totalPaginas={totalPaginas}
                onCambiar={cambiarPagina}
              />
            </>
          )}
        </div>
      </div>

      {mostrarModal && (
        <div className="modal-overlay">
          <div className="modal-custom">
            <div className="modal-header-custom">
              <h5>{editando ? 'Editar Comprobante' : 'Nuevo Comprobante'}</h5>
              <button className="btn-close-custom" onClick={cerrarModal}>×</button>
            </div>
            <div className="modal-body-custom">
              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Tipo *</label>
                    <select
                      name="tipo"
                      className={`form-select ${errores.tipo ? 'is-invalid' : ''}`}
                      value={form.tipo}
                      onChange={handleChange}
                    >
                      <option value="">Seleccionar tipo</option>
                      <option value="factura">Factura</option>
                      <option value="boleta">Boleta</option>
                      <option value="recibo">Recibo</option>
                      <option value="nota_credito">Nota de Crédito</option>
                    </select>
                    {errores.tipo && <div className="invalid-feedback">{errores.tipo}</div>}
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">Serie</label>
                    <input
                      name="serie"
                      className="form-control"
                      value={form.serie}
                      onChange={handleChange}
                      placeholder="F001"
                      maxLength={5}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label fw-bold">N° Comprobante</label>
                    <input
                      name="numero_comprobante"
                      className="form-control"
                      value={form.numero_comprobante}
                      onChange={handleChange}
                      placeholder="00001"
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Monto Total *</label>
                    <input
                      name="monto_total"
                      type="number"
                      step="0.01"
                      min="0.01"
                      className={`form-control ${errores.monto_total ? 'is-invalid' : ''}`}
                      value={form.monto_total}
                      onChange={handleChange}
                    />
                    {errores.monto_total && <div className="invalid-feedback">{errores.monto_total}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold">IGV</label>
                    <input
                      name="igv"
                      type="number"
                      step="0.01"
                      className="form-control"
                      value={form.igv}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-bold">Fecha Emisión *</label>
                    <input
                      name="fecha_emision"
                      type="date"
                      className={`form-control ${errores.fecha_emision ? 'is-invalid' : ''}`}
                      value={form.fecha_emision}
                      onChange={handleChange}
                    />
                    {errores.fecha_emision && <div className="invalid-feedback">{errores.fecha_emision}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Categoría</label>
                    <select
                      name="categoria_id"
                      className="form-select"
                      value={form.categoria_id}
                      onChange={handleChange}
                    >
                      <option value="">Sin categoría</option>
                      {categorias.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Proveedor</label>
                    <select
                      name="proveedor_id"
                      className="form-select"
                      value={form.proveedor_id}
                      onChange={handleChange}
                    >
                      <option value="">Sin proveedor</option>
                      {proveedores.map((p) => (
                        <option key={p.id} value={p.id}>{p.razon_social}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Archivo (PDF/Imagen)</label>
                    <input
                      type="file"
                      className="form-control"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setArchivoSeleccionado(e.target.files[0] || null)}
                    />
                    {archivoSeleccionado && (
                      <div className="mt-1 small text-success">
                        Archivo seleccionado: {archivoSeleccionado.name}
                      </div>
                    )}
                    {!archivoSeleccionado && editando?.archivo_url && editando.archivo_url.trim() !== '' && (
                      <div className="mt-1 small text-muted">
                        Ya tiene archivo adjunto. Sube uno nuevo para reemplazarlo.
                      </div>
                    )}
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-bold">Descripción</label>
                    <textarea
                      name="descripcion"
                      className="form-control"
                      rows={2}
                      value={form.descripcion}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 justify-content-end mt-4">
                  <button type="button" className="btn btn-secondary" onClick={cerrarModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-danger" disabled={guardando}>
                    {guardando ? 'Guardando...' : editando ? 'Actualizar' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {vistaPrevia && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}
          onClick={() => setVistaPrevia(null)}>
          <div style={{
            background: '#fff', borderRadius: 12,
            width: '90%', maxWidth: 800,
            maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
          }}
            onClick={e => e.stopPropagation()}>
            <div style={{
              padding: '12px 20px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>
                  Vista previa — {vistaPrevia.tipo} {vistaPrevia.serie}-{vistaPrevia.numero_comprobante || '—'}
                </div>
                <div style={{ fontSize: 12, color: '#aaa' }}>
                  {formatearMoneda(vistaPrevia.monto_total)} · {formatearFecha(vistaPrevia.fecha_emision)}
                </div>
              </div>
              <div className="d-flex gap-2">
                <a
                  href={urlArchivo(vistaPrevia.archivo_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm btn-outline-primary"
                  style={{ fontSize: 12 }}
                >
                  Abrir archivo
                </a>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setVistaPrevia(null)}
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
              {vistaPrevia.archivo_url?.includes('pdf') ? (
                <iframe
                  src={urlArchivo(vistaPrevia.archivo_url)}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                  title="Vista previa PDF"
                />
              ) : (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <img
                    src={urlArchivo(vistaPrevia.archivo_url)}
                    alt="Comprobante"
                    style={{
                      maxWidth: '100%', maxHeight: '65vh',
                      objectFit: 'contain', borderRadius: 8,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}